import React, { Suspense, lazy, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Sidebar } from "../components/Sidebar";
import { PageHeader, LoadingState, ErrorState, Tabs } from "../components/UI";
import axios from "axios";
import { useAuth } from "react-oidc-context";
import GlobalRoomsTable from "../components/UnifiedHotel/GlobalRoomsTable";
import { useUnifiedHotelRooms, ROOMS_DEFAULT_LIMIT } from "../components/UnifiedHotel/hooks/useUnifiedHotelRooms";
import RoomsCsvImport from "../components/UnifiedHotel/RoomsCsvImport";
import FeatureStatusSelector from "../components/UnifiedHotel/FeatureStatusSelector";
import HotelGeneralSettings from "../components/UnifiedHotel/HotelGeneralSettings";

// Onglets chargés à la demande. "Général" (RoomsCsvImport + GlobalRoomsTable) reste
// statique : c'est l'onglet par défaut, le rendre paresseux n'ajouterait qu'un spinner.
const LuniShowerContent = lazy(() => import("../components/UnifiedHotel/LuniShowerContent"));
const GreenMilesContent = lazy(() => import("../components/UnifiedHotel/GreenMilesContent"));
const PerformanceTrackingTab = lazy(() => import("../components/UnifiedHotel/PerformanceTrackingTab"));
const MailsTab = lazy(() => import("../components/UnifiedHotel/MailsTab"));

const PREFETCH = {
  lunishower: () => import("../components/UnifiedHotel/LuniShowerContent"),
  greenmiles: () => import("../components/UnifiedHotel/GreenMilesContent"),
  performance: () => import("../components/UnifiedHotel/PerformanceTrackingTab"),
  mails: () => import("../components/UnifiedHotel/MailsTab"),
};

const TABS = [
  { value: "general", label: "Général" },
  { value: "lunishower", label: "LuniShower" },
  { value: "greenmiles", label: "GreenMiles" },
  { value: "performance", label: "Suivi de performance" },
  { value: "mails", label: "Mails success" },
];

const GENERAL_TABS = [
  { value: "rooms", label: "Chambres" },
  { value: "settings", label: "Réglages" },
];

/**
 * Page hôtel unifiée : Général | LuniShower | GreenMiles | Suivi de performance | Mails success.
 * - Mails success : sélection du type de mail + prévisualisation HTML.
 */
function UnifiedHotelPage() {
  const { id: masterId, greenMilesTab } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const token = auth.user?.access_token;
  const API_URL = process.env.REACT_APP_API_URL || "/api";

  const [hotel, setHotel] = useState(null);
  const [loadingHotel, setLoadingHotel] = useState(true);
  const [hydratingHotelFeatures, setHydratingHotelFeatures] = useState(false);
  const [error, setError] = useState(null);
  const getTabFromUrl = () => {
    if (greenMilesTab || location.pathname.includes(`/hotels/${masterId}/greenmiles`)) {
      return "greenmiles";
    }
    const p = new URLSearchParams(location.search);
    const t = p.get("tab");
    return t === "lunishower" || t === "greenmiles" || t === "general" || t === "performance" || t === "mails" ? t : "general";
  };
  const [activeTab, setActiveTab] = useState(getTabFromUrl);
  const [roomsPage, setRoomsPage] = useState(1);
  const [roomsLimit, setRoomsLimit] = useState(ROOMS_DEFAULT_LIMIT);
  const [activeGeneralTab, setActiveGeneralTab] = useState("rooms");
  const [addingLuniShower, setAddingLuniShower] = useState(false);
  const [addLuniShowerError, setAddLuniShowerError] = useState(null);
  const [isMaintenance, setIsMaintenance] = useState(false);

  useEffect(() => {
    setActiveTab(getTabFromUrl());
  }, [location.pathname, location.search, greenMilesTab]);

  const {
    rooms,
    total_count,
    page,
    total_pages,
    limit,
    app_id,
    manager_id,
    has_lunishower,
    has_greenmiles,
    loading: loadingRooms,
    refetch,
  } = useUnifiedHotelRooms(masterId, {
    limit: roomsLimit,
    page: roomsPage,
    // Ces chambres n'alimentent que l'onglet "Général" : inutile de les charger quand on
    // arrive directement sur /hotels/:id/greenmiles ou ?tab=lunishower.
    enabled: activeTab === "general",
  });

  const fetchHotel = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoadingHotel(true);
      setError(null);
      const res = await axios.get(`${API_URL}/home/hotel/${masterId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success === "OK" && res.data?.result) {
        setHotel(res.data.result);
        return true;
      } else {
        setError("Hôtel non trouvé");
        return false;
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Erreur lors du chargement");
      return false;
    } finally {
      if (!silent) setLoadingHotel(false);
    }
  }, [masterId, token, API_URL]);

  useEffect(() => {
    const stateHotel = location.state?.hotel;

    // On accepte désormais un state partiel : la garde qui exigeait has_lunishower /
    // has_greenmiles / manager_id existait pour éviter une barre d'onglets fausse, or
    // `featuresUnknown` s'en charge maintenant (onglets affichés mais désactivés tant que
    // `hydratingHotelFeatures` est vrai). Sans cet assouplissement, arriver depuis la liste
    // avec un state incomplet retombait sur le chemin bloquant.
    if (stateHotel && String(stateHotel.id) === String(masterId)) {
      setHotel({
        id: stateHotel.id,
        name: stateHotel.name,
        tag: stateHotel.tag,
        nb_rooms: stateHotel.room_count ?? stateHotel.nb_rooms,
        has_lunishower: stateHotel.has_lunishower,
        has_greenmiles: stateHotel.has_greenmiles,
        has_lunibutton: stateHotel.has_lunibutton,
        lunishower_status: stateHotel.lunishower_status,
        greenmiles_status: stateHotel.greenmiles_status,
        lunibutton_status: stateHotel.lunibutton_status,
        manager_id: stateHotel.manager_id,
      });
      setLoadingHotel(false);
      if (token) {
        setHydratingHotelFeatures(true);
        fetchHotel({ silent: true }).finally(() => {
          setHydratingHotelFeatures(false);
        });
      }
      return () => {};
    }

    let cancelled = false;
    const run = async () => {
      try {
        if (!token) return;
        setLoadingHotel(true);
        setError(null);
        const res = await axios.get(`${API_URL}/home/hotel/${masterId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && res.data?.success === "OK" && res.data?.result) {
          setHotel(res.data.result);
        } else if (!cancelled) {
          setError("Hôtel non trouvé");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || "Erreur lors du chargement");
        }
      } finally {
        if (!cancelled) setLoadingHotel(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [masterId, token, API_URL, location.state, fetchHotel]);

  useEffect(() => {
    let cancelled = false;

    const fetchMaintenance = async () => {
      if (!masterId || !token) return;
      try {
        const res = await axios.get(`${API_URL}/greenmiles/hotels/${masterId}/maintenance-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setIsMaintenance(Boolean(res.data?.enabled));
        }
      } catch (err) {
        if (!cancelled) {
          setIsMaintenance(false);
        }
      }
    };

    fetchMaintenance();
    return () => {
      cancelled = true;
    };
  }, [masterId, token, API_URL]);

  const handleAddLuniShower = async () => {
    if (!masterId || addingLuniShower) return;
    setAddLuniShowerError(null);
    setAddingLuniShower(true);
    try {
      const res = await axios.post(
        `${API_URL}/hotels/${masterId}/add-lunishower-feature`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.data?.success === "OK") {
        await fetchHotel();
        setActiveTab("lunishower");
      } else {
        setAddLuniShowerError(res.data?.message || "Erreur lors de l'ajout");
      }
    } catch (err) {
      setAddLuniShowerError(err.response?.data?.message || err.message || "Erreur lors de l'ajout de la feature LuniShower");
    } finally {
      setAddingLuniShower(false);
    }
  };

  // Rien ne bloque le rendu de la coquille : titre, badges et barre d'onglets s'affichent
  // dès la première frame, les données arrivent ensuite.
  // - `featuresUnknown` : on ne sait pas encore quelles features l'hôtel possède, donc on
  //   montre tous les onglets mais on ne laisse cliquer que ceux qui n'en dépendent pas.
  // - `notFound` : distinct de l'erreur réseau, et jamais affiché pendant le chargement.
  const hasLuniShower = Boolean(hotel?.has_lunishower);
  const hasGreenMiles = Boolean(hotel?.has_greenmiles);
  const hasLuniButton = Boolean(hotel?.has_lunibutton);
  const featuresUnknown = !hotel || hydratingHotelFeatures;
  const notFound = !loadingHotel && !hotel && !error;
  const FEATURE_INDEPENDENT_TABS = ["general", "performance"];

  // Retour vers la liste d'où l'on vient, à défaut la liste des hôtels. On n'utilise plus
  // `navigate(-1)` : sur un F5, un lien partagé ou un accès direct, il n'y a pas d'entrée
  // précédente dans l'historique de l'app et on en sortait.
  const handleGoBack = () => {
    navigate(location.state?.from || "/hotels");
  };
  const handleTabChange = (value) => {
    setActiveTab(value);
    // On propage `location.state` : sans ça, changer d'onglet perdait à la fois l'origine
    // du retour et l'hôtel pré-rempli, ce qui refaisait passer la page par l'écran d'attente.
    const navOptions = { state: location.state };
    if (value === "greenmiles") {
      navigate(`/hotels/${masterId}/greenmiles`, navOptions);
      return;
    }
    const params = new URLSearchParams(location.search);
    params.set("tab", value);
    params.delete("greenMilesTab");
    const nextSearch = params.toString();
    navigate(`/hotels/${masterId}${nextSearch ? `?${nextSearch}` : ""}`, navOptions);
  };

  const performanceActivePacks = [];
  if (hasGreenMiles) {
    performanceActivePacks.push("greenmiles_base", "greenmiles_luniclean");
  }
  if (hasLuniShower) {
    performanceActivePacks.push("greenmiles_lunishower");
  }

  // Tant que les features sont inconnues on affiche la barre complète plutôt qu'une barre
  // qui se remplit sous le curseur : les libellés sont stables, seuls les états changent.
  const visibleTabs = featuresUnknown
    ? TABS
    : TABS.filter((t) => {
        if (t.value === "general") return true;
        if (t.value === "lunishower") return hasLuniShower && hotel.manager_id;
        if (t.value === "greenmiles") return hasGreenMiles;
        if (t.value === "performance") return true;
        if (t.value === "mails") return (hasLuniShower && hotel.manager_id) || hasGreenMiles;
        return false;
      });

  const disabledTabs = featuresUnknown ? TABS.filter((t) => !FEATURE_INDEPENDENT_TABS.includes(t.value)).map((t) => t.value) : [];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-shrink-0 p-6 pb-0">
          <PageHeader
            title={
              <span className="inline-flex flex-wrap items-center gap-2 align-middle">
                <span className={`leading-tight ${hotel ? "" : "text-gray-400"}`}>{hotel?.name ?? "Chargement de l'hôtel…"}</span>
                <span className="inline-flex items-center self-center rounded-md bg-gray-100 px-2 py-0.5 text-xs font-semibold leading-none text-gray-600 ring-1 ring-inset ring-gray-200">
                  ID {masterId}
                </span>
              </span>
            }
            onGoBack={handleGoBack}
            actions={
              <div className="mt-4 flex items-center gap-3 flex-nowrap whitespace-nowrap">
                {isMaintenance && <span className="inline-flex items-center px-3 py-1.5 rounded-lg border text-sm font-semibold bg-red-100 text-red-700 border-red-300">Hôtel en maintenance</span>}
                {hasLuniShower && <FeatureStatusSelector feature="lunishower" status={hotel.lunishower_status} masterId={masterId} onUpdate={fetchHotel} token={token} API_URL={API_URL} />}
                {hasGreenMiles && <FeatureStatusSelector feature="greenmiles" status={hotel.greenmiles_status} masterId={masterId} onUpdate={fetchHotel} token={token} API_URL={API_URL} />}
                {hasLuniButton && <FeatureStatusSelector feature="lunibutton" status={hotel.lunibutton_status} masterId={masterId} onUpdate={fetchHotel} token={token} API_URL={API_URL} />}
                {/* `featuresUnknown` est indispensable ici : sans lui, le bouton apparaîtrait
                    à tort pendant le chargement sur un hôtel qui a déjà LuniShower. */}
                {!featuresUnknown && !hasLuniShower && (
                  <div className="flex flex-col items-end gap-1">
                    <button type="button" onClick={handleAddLuniShower} disabled={addingLuniShower} className="px-4 py-2 bg-[#365644] text-white rounded-lg shadow hover:bg-[#2b4737] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                      {addingLuniShower ? "Ajout en cours..." : "Ajouter la feature LuniShower"}
                    </button>
                    {addLuniShowerError && <span className="text-sm text-red-600">{addLuniShowerError}</span>}
                  </div>
                )}
              </div>
            }
          />

          <div className="mt-4">
            <Tabs variant="primary" tabs={visibleTabs} value={activeTab} onChange={handleTabChange} disabledValues={disabledTabs} onTabHover={(value) => PREFETCH[value]?.()} />
          </div>
        </div>

        {/* Suspense sous la barre d'onglets : elle reste visible pendant le chargement du chunk. */}
        <Suspense fallback={<div className="p-6 pt-4"><LoadingState embedded message="Chargement..." /></div>}>
        <div className="flex-1 overflow-auto min-h-0">
          {error && (
            <div className="p-6 pt-4">
              <ErrorState error={error} embedded />
            </div>
          )}
          {notFound && (
            <div className="p-6 pt-4">
              <ErrorState error="Hôtel non trouvé" embedded />
            </div>
          )}

          {activeTab === "general" && (
            <div className="p-6 pt-4">
              <Tabs variant="secondary" tabs={GENERAL_TABS} value={activeGeneralTab} onChange={setActiveGeneralTab} />

              {activeGeneralTab === "rooms" && (
                <>
                  <RoomsCsvImport
                    masterId={masterId}
                    onSuccess={() => {
                      refetch();
                    }}
                  />
                  <GlobalRoomsTable
                    rooms={rooms}
                    total_count={total_count}
                    page={page}
                    total_pages={total_pages}
                    limit={limit}
                    onPageChange={setRoomsPage}
                    onLimitChange={(newLimit) => {
                      setRoomsLimit(newLimit);
                      setRoomsPage(1);
                    }}
                    app_id={app_id}
                    manager_id={manager_id}
                    masterId={masterId}
                    has_lunishower={has_lunishower}
                    has_greenmiles={has_greenmiles}
                    loading={loadingRooms}
                    onRefetch={refetch}
                  />
                </>
              )}

              {activeGeneralTab === "settings" &&
                (featuresUnknown ? (
                  <LoadingState embedded message="Chargement des réglages..." />
                ) : (
                  <HotelGeneralSettings
                    masterId={masterId}
                    initialTag={hotel.tag}
                    initialCity={hotel.city}
                    token={token}
                    API_URL={API_URL}
                    onUpdate={fetchHotel}
                  />
                ))}
            </div>
          )}

          {/* Les 3 onglets suivants dépendent des features : on attend `hotel` pour savoir
              quoi monter, mais l'attente reste confinée au panneau — la barre d'onglets et
              l'en-tête restent affichés. */}
          {activeTab === "lunishower" && (
            <div className="p-6 pt-4">
              {featuresUnknown ? <LoadingState embedded message="Chargement de l'hôtel..." /> : hasLuniShower && hotel.manager_id ? <LuniShowerContent hotelId={hotel.manager_id} luniAppHotelId={masterId} greenMilesDbName={hotel.name} /> : null}
            </div>
          )}

          {activeTab === "greenmiles" && (
            <div className="p-6 pt-4">
              {featuresUnknown ? <LoadingState embedded message="Chargement de l'hôtel..." /> : hasGreenMiles ? <GreenMilesContent hotelId={masterId} /> : null}
            </div>
          )}

          {activeTab === "performance" && (
            <div className="p-6 pt-4">
              {/* Garde obligatoire, pas cosmétique : sans `hotel`, `performanceActivePacks`
                  serait vide et le tableau se monterait avec de mauvais packs. */}
              {featuresUnknown ? (
                <LoadingState embedded message="Chargement des performances GreenMiles..." />
              ) : (
                <PerformanceTrackingTab
                  hotelName={hotel.name}
                  hotelId={masterId}
                  activePacks={performanceActivePacks}
                />
              )}
            </div>
          )}

          {activeTab === "mails" && (
            <div className="p-6 pt-4">
              {featuresUnknown ? (
                <LoadingState embedded message="Chargement de l'hôtel..." />
              ) : (hasLuniShower && hotel.manager_id) || hasGreenMiles ? (
                <MailsTab
                  token={token}
                  API_URL={API_URL}
                  hotelId={masterId}
                  hotelName={hotel.name}
                  hasLuniShower={hasLuniShower && hotel.manager_id}
                  hasGreenMiles={hasGreenMiles}
                />
              ) : null}
            </div>
          )}
        </div>
        </Suspense>
      </div>
    </div>
  );
}

export default UnifiedHotelPage;
